import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { EmailComposer } from '@awesome-cordova-plugins/email-composer/ngx';
import { TranslateModule, TranslateService } from '@jht/brand-translate';
import { BackgroundMode } from '@awesome-cordova-plugins/background-mode/ngx';

import { CoreModule } from './core/core.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { DataService } from './services/data.service';
import { SharedModule } from './shared/shared.module';
import { ActiveUserService } from './services/active-user.service';
import { AuthenticationService } from './services/authentication.service';
import { APP_STATE_KEYS, meaDefaultRegion } from './models';
import { StateService } from './services/state.service';
import { environment } from 'src/environments/environment';


export const setupTranslateConfig = (dataService: DataService) => {
    const fallbackLanguages = dataService.fallbackLanguages;
    return {
        siteConfig: {
            fallbackLanguages,
            regionLanguage: fallbackLanguages[0],
        },
        translations: {
            dictionary: dataService?.translationsData,
        },
    };
};

export const dataStartupInitializer = async (): Promise<void> => {
    const authenticationService = inject(AuthenticationService);
    const activeUserService = inject(ActiveUserService);
    const dataService = inject(DataService);

    const auth = await authenticationService.isAuthenticated();
    const region = (auth && activeUserService?.currentUserValue?.user_metadata?.region)
        ? activeUserService.currentUserValue.user_metadata.region
        : meaDefaultRegion;

    await dataService.initSiteWideData(region);
};

/** Reset the APP_IS_OUTDATED state after a new version installation */
export const verifyAppVersionInitializer = async (): Promise<void> => {
    const stateService = inject(StateService);

    const lastAppVersion = await stateService.get(APP_STATE_KEYS.APP_LAST_VERSION);
    const currentVersion = environment.version;

    if (lastAppVersion !== currentVersion) {
        await Promise.all([
            stateService.set(APP_STATE_KEYS.APP_LAST_VERSION, currentVersion),
            stateService.set(APP_STATE_KEYS.APP_IS_OUTDATED, 'false')
        ]);
    }
};

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    exports: [], 
    imports: [BrowserModule,
        CoreModule,
        IonicModule.forRoot(),
        AppRoutingModule,
        SharedModule,
        TranslateModule], 
    providers: [
        TranslateService,
        {
            provide: "WINDOW",
            useFactory: () => window
        },
        {
            provide: 'translateConfig',
            useFactory: setupTranslateConfig,
            deps: [DataService],
        },
        provideAppInitializer(dataStartupInitializer),
        provideAppInitializer(verifyAppVersionInitializer),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        EmailComposer,
        BackgroundMode,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())
    ] })
export class AppModule { }
